import type { AgentsBlockProps, Agent } from '../types';
import { theme, sectionColors } from '../theme';

/** Two-letter initials for the no-photo avatar fallback. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Pure agent roster. Like the listings block the renderer is "dumb": it receives
 * `agents` from the host (tenant-sites AND the builder preview both call
 * GET /mls/agents, which aggregates EVERY office ID configured on the tenant's
 * MLS integration — so multi-office brokerages show all their agents) and NEVER
 * fetches. Renders a graceful empty state rather than throwing. Framework-agnostic
 * (plain <img>) so it transpiles in both Next.js and Vite.
 */
export function AgentsBlock({ props, agents }: { props: AgentsBlockProps; agents: Agent[] }) {
  const { heading, limit, layout = 'grid', showContact = true } = props;
  const items = typeof limit === 'number' ? agents.slice(0, limit) : agents;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });

  if (items.length === 0) {
    return (
      <section className="px-6 py-12 text-center" style={{ ...colors, fontFamily: theme.bodyFont }}>
        <p className="opacity-60">No agents to show yet.</p>
      </section>
    );
  }

  const isList = layout === 'list';
  const grid = isList ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      {heading ? (
        <h2 className="mx-auto mb-8 max-w-6xl text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
          {heading}
        </h2>
      ) : null}
      <div className={`mx-auto grid max-w-6xl gap-6 ${grid}`}>
        {items.map((a) => (
          // `tmpl-agent-*` class hooks let the site's chosen Agent Card style (Card Style picker →
          // section_looks.agentCard → a `data-tmpl-agent-card` ancestor + globals.css) reshape these
          // cards. With no such ancestor the hooks are inert and the default look is unchanged.
          <div
            key={a.key || a.name}
            className={`tmpl-agent-card overflow-hidden rounded-lg border border-black/10 ${isList ? 'flex items-center gap-4 p-4' : ''}`}
            style={{ backgroundColor: theme.surface }}
          >
            <div className={`tmpl-agent-media relative overflow-hidden ${isList ? 'h-20 w-20 shrink-0' : ''}`}>
              {a.photo ? (
                <img
                  src={a.photo}
                  alt={a.name}
                  referrerPolicy="no-referrer"
                  loading="lazy"
                  decoding="async"
                  className={`tmpl-agent-img object-cover ${isList ? 'h-20 w-20 rounded-full' : 'h-56 w-full'}`}
                />
              ) : (
                <div
                  className={`tmpl-agent-avatar flex items-center justify-center font-semibold text-white ${
                    isList ? 'h-20 w-20 rounded-full text-xl' : 'h-56 w-full text-4xl'
                  }`}
                  style={{ backgroundColor: theme.primary, fontFamily: theme.headingFont }}
                  aria-hidden="true"
                >
                  {initialsOf(a.name)}
                </div>
              )}
              {/* Hover reveal for the Overlay agent-card style — hidden unless a data-tmpl-agent-card
                  ancestor turns it on, so it costs nothing on every other style. */}
              <div className="tmpl-agent-reveal hidden" aria-hidden="true">
                <span className="tmpl-agent-reveal-name">{a.name}</span>
                {a.title ? <span className="tmpl-agent-reveal-title">{a.title}</span> : null}
                {showContact && (a.phone || a.email) ? (
                  <ul className="tmpl-agent-reveal-contact">
                    {a.phone ? <li>{a.phone}</li> : null}
                    {a.email ? <li>Email</li> : null}
                  </ul>
                ) : null}
                <span className="tmpl-agent-reveal-cta">Contact</span>
              </div>
            </div>
            <div className={`tmpl-agent-body ${isList ? 'min-w-0' : 'p-4'}`}>
              <div className="tmpl-agent-name text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>
                {a.name}
              </div>
              {a.title ? <div className="tmpl-agent-title text-sm opacity-70">{a.title}</div> : null}
              {a.office ? <div className="tmpl-agent-office text-sm opacity-60">{a.office}</div> : null}
              {showContact && (a.phone || a.email) ? (
                <div className="tmpl-agent-contact mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {a.phone ? (
                    <a
                      href={`tel:${a.phone.replace(/[^\d+]/g, '')}`}
                      className="tmpl-agent-link font-medium underline-offset-2 hover:underline"
                      style={{ color: theme.primary }}
                    >
                      {a.phone}
                    </a>
                  ) : null}
                  {a.email ? (
                    <a
                      href={`mailto:${a.email}`}
                      className="tmpl-agent-link font-medium underline-offset-2 hover:underline"
                      style={{ color: theme.primary }}
                    >
                      Email
                    </a>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
